import type { SceneNode } from "@uzverse/world-scene";
import { FLOOR_HEIGHT_M } from "@uzverse/world-scene";

type Construction = NonNullable<SceneNode["construction"]>;

/** Progress 0..1 of a construction effect at server time `serverNowMs` (epoch ms). */
export function constructionProgress(c: Construction, serverNowMs: number): number {
  const started = Date.parse(c.startedAt);
  if (!Number.isFinite(started)) return 1;
  return Math.min(1, Math.max(0, (serverNowMs - started) / c.durationMs));
}

/**
 * Height (local Y, meters) up to which the building is revealed, or null when it is complete.
 * `build` rises from the ground, `floor` raises only the new floor (everything below it is
 * already there), `demolish` plays the build backwards.
 */
export function revealHeight(
  c: Construction | undefined,
  totalHeight: number,
  serverNowMs: number,
): number | null {
  if (!c) return null;
  const p = constructionProgress(c, serverNowMs);
  if (c.kind === "build") return p * totalHeight;
  if (c.kind === "demolish") return (1 - p) * totalHeight;
  const base = Math.max(0, (c.level ?? 0) * FLOOR_HEIGHT_M);
  return Math.min(totalHeight, base + p * (totalHeight - base));
}

/** Seconds-accurate server clock: `serverNow = Date.now() + offset`. */
export function serverOffset(serverTime: string, localNowMs: number): number {
  const t = Date.parse(serverTime);
  return Number.isFinite(t) ? t - localNowMs : 0;
}
